import { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Activity, Heart, Droplet, Scale, Loader2, Ruler, Dumbbell } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import { getCustomerExams } from '@/lib/firebase/queries/exams/getExams';
import { cn } from '@/lib/utils';
import { translations } from '@/translations';
import { loadPerson } from '@/lib/firebase/queries/namatAssessment';
import {
  ageInMonths,
  readIndicators,
  type ExamValues,
  type Indicator,
  type IndicatorReading,
  type Level,
} from '@/lib/assessment/namatAssessment';
import { IndicatorBadge } from '@/components/health/IndicatorBadge';
import { ChangeMarker } from '@/components/health/ChangeMarker';
import { ExamReportDialog } from '@/components/health/ExamReportDialog';
import { buildExamReport, type ExamReport, type ReportExam } from '@/lib/assessment/examTrends';
import { indicatorUnit } from '@/components/health/indicatorUnit';

interface HealthMeasuresWidgetProps {
  organizationId?: string;
  customerId?: string; // Optional: if provided, use this instead of current user
  variant?: 'card' | 'inline'; // 'card' for dashboard, 'inline' for dialog
}

interface HealthMeasuresContentProps {
  customerId?: string; // Optional: if provided, use this instead of current user
  forceEnglish?: boolean; // If true, always display in English (for dialog)
}

interface HealthMetric {
  id: string;
  name: string;
  value: number | string;
  unit: string;
  status: Level | null;
  icon: React.ElementType;
  reading: IndicatorReading;
}

const ICONS: Record<Indicator, React.ElementType> = {
  bmi: Scale,
  bloodPressure: Heart,
  hba1c: Droplet,
  cholesterol: Activity,
  pulse: Heart,
  waistCircumference: Ruler,
  neckCircumference: Ruler,
  fitnessAge: Dumbbell,
};

function toMetrics(readings: IndicatorReading[], t: (key: string) => string): HealthMetric[] {
  return readings.map((reading) => ({
    id: reading.key,
    name: t(`common.health.indicators.${reading.key}`),
    value: reading.value,
    unit: indicatorUnit(reading, t),
    status: reading.level,
    icon: ICONS[reading.key],
    reading,
  }));
}

// Content component (used in dialog without Card wrapper)
export function HealthMeasuresContent({ customerId, forceEnglish = false }: HealthMeasuresContentProps) {
  const { t: tDefault } = useLanguage();
  const { user } = useAuth();
  
  // Create a translation function that uses English when forceEnglish is true
  // Memoize it to prevent infinite re-renders
  const t = useMemo(() => {
    return (key: string, params?: Record<string, string | number>) => {
      if (forceEnglish) {
        try {
          const keys = key.split('.');
          let value: unknown = translations.en;
          
          for (const k of keys) {
            if (value && typeof value === 'object' && k in value) {
              value = (value as Record<string, unknown>)[k];
            } else {
              return key;
            }
          }

          if (typeof value !== 'string') {
            return key;
          }

          if (params) {
            let text = value.replace(/\{\{(\w+)\}\}/g, (_, param) => 
              String(params[param] ?? `{{${param}}}`)
            );
            text = text.replace(/\{(\w+)\}/g, (_, param) => 
              String(params[param] ?? `{${param}}`)
            );
            return text;
          }

          return value;
        } catch {
          return key;
        }
      }
      return tDefault(key, params);
    };
  }, [forceEnglish, tDefault]);
  const [loading, setLoading] = useState(true);
  const [metrics, setMetrics] = useState<HealthMetric[]>([]);

  // Use provided customerId or fall back to current user
  const targetUserId = customerId || user?.uid;

  useEffect(() => {
    const loadExams = async () => {
      console.log('[HealthMeasuresContent] Loading exams for targetUserId:', targetUserId);
      console.log('[HealthMeasuresContent] customerId prop:', customerId);
      console.log('[HealthMeasuresContent] user?.uid:', user?.uid);
      console.log('[HealthMeasuresContent] forceEnglish:', forceEnglish);
      
      if (!targetUserId) {
        console.log('[HealthMeasuresContent] No targetUserId, setting loading to false');
        setLoading(false);
        return;
      }

      try {
        console.log('[HealthMeasuresContent] Setting loading to true');
        setLoading(true);
        console.log('[HealthMeasuresContent] Calling getCustomerExams with:', targetUserId);
        const exams = await getCustomerExams(targetUserId);
        console.log('[HealthMeasuresContent] Exams received:', exams);
        console.log('[HealthMeasuresContent] Number of exams:', exams.length);
        
        // Get the latest available examination
        // Sort all exams by date descending to ensure we get the most recent one
        const sortedExams = [...exams].sort((a, b) => {
          const dateA = a.date instanceof Date ? a.date : new Date(a.date);
          const dateB = b.date instanceof Date ? b.date : new Date(b.date);
          return dateB.getTime() - dateA.getTime();
        });
        console.log('[HealthMeasuresContent] Sorted exams:', sortedExams);
        
        // Prioritize completed exams, but use the latest one available
        const completedExams = sortedExams.filter(exam => exam.status === 'completed');
        console.log('[HealthMeasuresContent] Completed exams:', completedExams);
        const latest = completedExams.length > 0 
          ? completedExams[0] // Latest completed exam
          : sortedExams[0]; // If no completed exams, use the latest exam regardless of status
        console.log('[HealthMeasuresContent] Latest exam:', latest);
          
        if (latest) {
          
          // Levels come from the shared module (#91), age at the exam date.
          const person = await loadPerson(targetUserId);
          const examDate = latest.date instanceof Date ? latest.date : new Date(latest.date);
          const ageMonths = person.birthDate ? ageInMonths(person.birthDate, examDate) : null;
          setMetrics(toMetrics(readIndicators(latest as unknown as ExamValues, { ageMonths, sex: person.sex }), t));
        } else {
          console.log('[HealthMeasuresContent] No latest exam found');
        }
      } catch (error) {
        console.error('[HealthMeasuresContent] Error loading health measures:', error);
        console.error('[HealthMeasuresContent] Error details:', error instanceof Error ? error.message : error);
        console.error('[HealthMeasuresContent] Error stack:', error instanceof Error ? error.stack : 'No stack');
      } finally {
        console.log('[HealthMeasuresContent] Setting loading to false');
        setLoading(false);
      }
    };

    loadExams();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetUserId]); // Only depend on targetUserId to avoid infinite re-renders


  if (loading) {
    return (
      <div className="flex justify-center items-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (metrics.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        {t('dashboard.customer.dashboard.healthMeasures.noData')}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {metrics.map((metric, index) => {
        const Icon = metric.icon;
        
        return (
          <motion.div
            key={metric.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="space-y-2"
          >
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-3 flex-1">
                 <div 
                   className={cn(
                     "w-10 h-10 rounded-full flex items-center justify-center",
                     metric.status === 'good' && "bg-[#7dda55]/15",
                     metric.status === 'acceptable' && "bg-yellow-500/15",
                     metric.status === 'bad' && "bg-red-500/15"
                   )}
                 >
                   <Icon 
                     className={cn(
                       "h-5 w-5",
                       metric.status === 'good' && "text-[#7dda55]",
                       metric.status === 'acceptable' && "text-yellow-500",
                       metric.status === 'bad' && "text-red-500"
                     )} 
                   />
                 </div>
                <div className="flex-1">
                  <div className="font-medium text-sm">{metric.name}</div>
                  <div className="text-xs text-muted-foreground">
                    {typeof metric.value === 'number' 
                      ? `${metric.value.toFixed(metric.value % 1 === 0 ? 0 : 1)} ${metric.unit}`
                      : `${metric.value} ${metric.unit}`
                    }
                  </div>
                </div>
              </div>
              <IndicatorBadge reading={metric.reading} t={t} />
            </div>
          </motion.div>
        );
      })}
    </div>
  );
}

export function HealthMeasuresWidget({ customerId }: HealthMeasuresWidgetProps) {
  const { t } = useLanguage();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [metrics, setMetrics] = useState<HealthMetric[]>([]);
  // Every completed exam over time (#112): change markers here, the report on tap.
  const [report, setReport] = useState<ExamReport | null>(null);
  const [reportOpen, setReportOpen] = useState(false);

  // Use provided customerId or fall back to current user
  const targetUserId = customerId || user?.uid;

  useEffect(() => {
    const loadExams = async () => {
      if (!targetUserId) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const exams = await getCustomerExams(targetUserId);
        
        // Get the latest available examination
        // Sort all exams by date descending to ensure we get the most recent one
        const sortedExams = [...exams].sort((a, b) => {
          const dateA = a.date instanceof Date ? a.date : new Date(a.date);
          const dateB = b.date instanceof Date ? b.date : new Date(b.date);
          return dateB.getTime() - dateA.getTime();
        });
        
        // Prioritize completed exams, but use the latest one available
        const completedExams = sortedExams.filter(exam => exam.status === 'completed');
        const latest = completedExams.length > 0 
          ? completedExams[0] // Latest completed exam
          : sortedExams[0]; // If no completed exams, use the latest exam regardless of status
          
        if (latest) {
          
          // Levels come from the shared module (#91), age at the exam date.
          const person = await loadPerson(targetUserId);
          const examDate = latest.date instanceof Date ? latest.date : new Date(latest.date);
          const ageMonths = person.birthDate ? ageInMonths(person.birthDate, examDate) : null;
          setMetrics(toMetrics(readIndicators(latest as unknown as ExamValues, { ageMonths, sex: person.sex }), t));
          setReport(buildExamReport(exams as unknown as ReportExam[], person));
        } else {
          console.log('[HealthMeasuresContent] No latest exam found');
        }
      } catch (error) {
        console.error('[HealthMeasuresContent] Error loading health measures:', error);
        console.error('[HealthMeasuresContent] Error details:', error instanceof Error ? error.message : error);
        console.error('[HealthMeasuresContent] Error stack:', error instanceof Error ? error.stack : 'No stack');
      } finally {
        console.log('[HealthMeasuresContent] Setting loading to false');
        setLoading(false);
      }
    };

    loadExams();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetUserId]); // Only depend on targetUserId to avoid infinite re-renders

  const hasReport = !!report && report.exams.length > 0;
  const changeOf = (key: string) => report?.series.find((s) => s.key === key)?.change;

  return (
    <Card className="overflow-hidden">
      <CardHeader className="border-b bg-muted/50">
        <div className="flex items-center justify-between gap-2">
          <CardTitle className="flex items-center gap-2">
            <Activity className="h-5 w-5 text-primary" />
            {t('dashboard.customer.dashboard.healthMeasures.title')}
          </CardTitle>
          {hasReport && (
            <button
              type="button"
              onClick={() => setReportOpen(true)}
              aria-label={t('common.health.report.openFor')}
              data-testid="health-measures-report-link"
              className="text-sm font-medium text-primary hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded"
            >
              {t('common.health.report.open')}
            </button>
          )}
        </div>
      </CardHeader>
      <CardContent
        className={cn('p-6', hasReport && 'cursor-pointer hover:bg-muted/30 transition-colors')}
        onClick={hasReport ? () => setReportOpen(true) : undefined}
      >
        {loading ? (
          <div className="flex justify-center items-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : metrics.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            {t('dashboard.customer.dashboard.healthMeasures.noData')}
          </div>
        ) : (
          <div className="space-y-4">
            {metrics.map((metric, index) => {
              const Icon = metric.icon;
              
              return (
                <motion.div
                  key={metric.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="space-y-2"
                >
                  <div className="flex items-center justify-between gap-4">
                    <div className="flex items-center gap-3 flex-1">
                 <div 
                   className={cn(
                     "w-10 h-10 rounded-full flex items-center justify-center",
                     metric.status === 'good' && "bg-[#7dda55]/15",
                     metric.status === 'acceptable' && "bg-yellow-500/15",
                     metric.status === 'bad' && "bg-red-500/15"
                   )}
                 >
                   <Icon 
                     className={cn(
                       "h-5 w-5",
                       metric.status === 'good' && "text-[#7dda55]",
                       metric.status === 'acceptable' && "text-yellow-500",
                       metric.status === 'bad' && "text-red-500"
                     )} 
                   />
                 </div>
                      <div className="flex-1">
                        <div className="font-medium text-sm">{metric.name}</div>
                        <div className="text-xs text-muted-foreground flex flex-wrap items-center gap-x-2">
                          <span>
                            {typeof metric.value === 'number'
                              ? `${metric.value.toFixed(metric.value % 1 === 0 ? 0 : 1)} ${metric.unit}`
                              : `${metric.value} ${metric.unit}`
                            }
                          </span>
                          {changeOf(metric.id) && <ChangeMarker change={changeOf(metric.id)!} t={t} />}
                        </div>
                      </div>
                    </div>
                    <IndicatorBadge reading={metric.reading} t={t} />
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}
      </CardContent>
      {targetUserId && (
        <ExamReportDialog open={reportOpen} onOpenChange={setReportOpen} customerId={targetUserId} report={report} />
      )}
    </Card>
  );
}

